'use strict';

const libCustomerConfigScreen = require('./CustomerConfigScreen-Provider.js');
const libScanStatus = require('headlight-sample-scanning/source/Sample-Scan-Status.js');
const { DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS } = require('headlight-dynamic-inputs-bundle/source/providers/_VocabularySelectorMap.js');

// Fieldbook iOS reads `Features.QRScanner` from FieldbookiOS (mpi_ios ManifestsSyncOperation.m, QR_SCANNER_CONFIG_KEY).
const IOS_NAMESPACE = 'FieldbookiOS';
const IOS_FEATURES = 'Features';
const IOS_SCANNER_KEY = 'QRScanner';
// Read by headlight-sample-scanning Sample-Scan-Status.js when a sample is moved into a location.
const RECEIVE_NAMESPACE = 'QRCodes';
const RECEIVE_VALUE = 'Receive';
const NO_STATUS = '';

/**
 * @typedef {{ Key: string, Name: string, QRCode: string, Type: string, IsTop: boolean }} ScanLocation
 * @typedef {{ Hash: string, Name: string }} ScanStatus
 */

/**
 * The location key a Receive rule is stored under: its QR code, or its GUID when it has none. The scanner matches
 * either (Sample-Scan-Status.js getConfiguredStatus).
 *
 * @param {{ QRCode?: string, GUIDLocation?: string }} pLocation
 * @return {string}
 */
function locationKey(pLocation)
{
	return String((pLocation && (pLocation.QRCode || pLocation.GUIDLocation)) || '');
}

/**
 * The Receive value to save: the edited rules and, when it was set or changed, the statuses they may replace. Keys
 * the screen has no control for are kept.
 *
 * @param {Record<string, any>} pLoaded - the value as read
 * @param {Record<string, string>} pStatusByLocation
 * @param {Array<string>|null} pOnlySetFrom - null leaves the reader's default (only samples with no status)
 * @return {Record<string, any>}
 */
function buildReceiveConfig(pLoaded, pStatusByLocation, pOnlySetFrom)
{
	const tmpConfig = Object.assign({}, pLoaded || {});
	tmpConfig.StatusByLocation = Object.assign({}, pStatusByLocation || {});
	if (Array.isArray(pOnlySetFrom))
	{
		tmpConfig.OnlySetFrom = pOnlySetFrom.slice();
	}
	else
	{
		delete tmpConfig.OnlySetFrom;
	}
	return tmpConfig;
}

/**
 * The FieldbookiOS Features value to save. The iPad keeps its last value when the key is missing, so off is
 * written as false, never by removing the key.
 *
 * @param {Record<string, any>} pLoaded
 * @param {boolean} pScanner
 * @return {Record<string, any>}
 */
function buildFeaturesConfig(pLoaded, pScanner)
{
	return Object.assign({}, pLoaded || {}, { [IOS_SCANNER_KEY]: !!pScanner });
}

/**
 * SampleScanningConfig: the #/SampleScanningConfig screen ("Sample Scanning"). Two customer settings that decide what
 * scanning does: whether Fieldbook on iPad shows its QR scanner, and the status a sample takes when it is scanned
 * into a location (`QRCodes` / `Receive`). Opened by the Configure sample QR codes entitlement.
 */
class SampleScanningConfigProvider extends libCustomerConfigScreen
{
	/** @return {Record<string, any>} */
	initialState()
	{
		return {
			Features: {}, Scanner: null, ScannerDirty: false,
			Receive: {}, StatusByLocation: {}, OnlySetFrom: null, ReceiveDirty: false,
			Locations: [], Statuses: [], NewStatus: '',
		};
	}

	/**
	 * @param {number} pIDCustomer
	 * @return {Promise<void>}
	 */
	async loadCustomerAsync(pIDCustomer)
	{
		const tmpState = this.state;
		tmpState.Features = await this.readCustomerConfigAsync(IOS_NAMESPACE, IOS_FEATURES, pIDCustomer);
		this.rememberSnapshot(IOS_NAMESPACE, IOS_FEATURES, tmpState.Features);
		tmpState.Scanner = (typeof tmpState.Features[IOS_SCANNER_KEY] === 'boolean') ? tmpState.Features[IOS_SCANNER_KEY] : null;
		tmpState.ScannerDirty = false;

		tmpState.Receive = await this.readCustomerConfigAsync(RECEIVE_NAMESPACE, RECEIVE_VALUE, pIDCustomer);
		this.rememberSnapshot(RECEIVE_NAMESPACE, RECEIVE_VALUE, tmpState.Receive);
		tmpState.StatusByLocation = (tmpState.Receive.StatusByLocation && typeof tmpState.Receive.StatusByLocation === 'object') ? Object.assign({}, tmpState.Receive.StatusByLocation) : {};
		tmpState.OnlySetFrom = Array.isArray(tmpState.Receive.OnlySetFrom) ? tmpState.Receive.OnlySetFrom.map(String) : null;
		tmpState.ReceiveDirty = false;

		const tmpLocations = await this._getJSON(`Locations/FilteredTo/FBV~IDCustomer~EQ~${Number(pIDCustomer)}~FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/5000`);
		tmpState.Locations = (Array.isArray(tmpLocations) ? tmpLocations : [])
			.filter((pLocation) => locationKey(pLocation))
			.map((pLocation) => ({ Key: locationKey(pLocation), Name: pLocation.Name || locationKey(pLocation), QRCode: pLocation.QRCode || '', Type: pLocation.Type || '', IsTop: !(Number(pLocation.ParentIDLocation) > 0) }));

		const tmpStatuses = await this._getJSON('SampleStatuss/FilteredTo/FBV~Deleted~EQ~0~FSF~Ordinal~ASC~0/0/500');
		tmpState.Statuses = (Array.isArray(tmpStatuses) && tmpStatuses.length) ?
			tmpStatuses.filter((pStatus) => pStatus && pStatus.Hash).map((pStatus) => ({ Hash: String(pStatus.Hash), Name: pStatus.Name || pStatus.Hash })) :
			DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS.map((pOption) => ({ Hash: pOption.id, Name: pOption.text }));
		const tmpUsed = Object.values(tmpState.StatusByLocation)[0];
		tmpState.NewStatus = tmpUsed || ((tmpState.Statuses.find((pStatus) => pStatus.Hash === 'RECV') || tmpState.Statuses[0] || { Hash: '' }).Hash);
	}

	/** @return {Promise<void>} */
	async saveAsync()
	{
		const tmpState = this.state;
		if (tmpState.ScannerDirty)
		{
			await this.writeCustomerConfigAsync(IOS_NAMESPACE, IOS_FEATURES, buildFeaturesConfig(tmpState.Features, tmpState.Scanner));
			tmpState.Features = buildFeaturesConfig(tmpState.Features, tmpState.Scanner);
			tmpState.ScannerDirty = false;
		}
		if (tmpState.ReceiveDirty)
		{
			tmpState.Receive = buildReceiveConfig(tmpState.Receive, tmpState.StatusByLocation, tmpState.OnlySetFrom);
			await this.writeCustomerConfigAsync(RECEIVE_NAMESPACE, RECEIVE_VALUE, tmpState.Receive);
			tmpState.ReceiveDirty = false;
		}
	}

	/** @return {string} */
	savedMessage()
	{
		return 'Saved. Scans into a location follow these rules within about 15 minutes; iPads pick up the scanner setting the next time they sync.';
	}

	// --- Edits ----------------------------------------------------------------------------------------------------

	/** @param {boolean} pOn */
	setScanner(pOn)
	{
		this.state.Scanner = !!pOn;
		this.state.ScannerDirty = true;
		this.markDirty();
		this.render();
	}

	/** Note an unsaved change to the receiving rules. */
	_receiveEdited()
	{
		this.state.ReceiveDirty = true;
		this.markDirty();
	}

	/**
	 * @param {string} pKey - a location key
	 * @param {string} pStatus
	 */
	setLocationStatus(pKey, pStatus)
	{
		if (!pStatus)
		{
			return;
		}
		this.state.StatusByLocation[pKey] = String(pStatus);
		this._receiveEdited();
		this.render();
	}

	/** @param {string} pKey */
	removeLocation(pKey)
	{
		delete this.state.StatusByLocation[pKey];
		this._receiveEdited();
		this.render();
	}

	/** @param {string} pStatus - the status rows added from now on set */
	setNewStatus(pStatus)
	{
		this.state.NewStatus = String(pStatus || '');
	}

	/** Give every top-level location that has no rule yet the chosen status. */
	addTopLevelLocations()
	{
		const tmpState = this.state;
		if (!tmpState.NewStatus)
		{
			return;
		}
		tmpState.Locations.filter((pLocation) => pLocation.IsTop && !tmpState.StatusByLocation[pLocation.Key]).forEach((pLocation) =>
		{
			tmpState.StatusByLocation[pLocation.Key] = tmpState.NewStatus;
		});
		this._receiveEdited();
		this.render();
	}

	/**
	 * @param {string} pStatus - a status hash, or '' for a sample with no status yet
	 * @param {boolean} pOn
	 */
	toggleOnlySetFrom(pStatus, pOn)
	{
		const tmpState = this.state;
		const tmpList = Array.isArray(tmpState.OnlySetFrom) ? tmpState.OnlySetFrom.slice() : libScanStatus.DEFAULT_ONLY_SET_FROM.slice();
		const tmpIndex = tmpList.indexOf(pStatus);
		if (pOn && (tmpIndex < 0))
		{
			tmpList.push(pStatus);
		}
		else if (!pOn && (tmpIndex >= 0))
		{
			tmpList.splice(tmpIndex, 1);
		}
		tmpState.OnlySetFrom = tmpList;
		this._receiveEdited();
		this.render();
	}

	/**
	 * @param {string} pPicker
	 * @param {string} pText
	 * @return {Array<{ Value: string, Label: string, Sub?: string }>} locations that don't set a status yet
	 */
	pickerChoices(pPicker, pText)
	{
		const tmpState = this.state;
		const tmpChoices = tmpState.Locations
			.filter((pLocation) => !tmpState.StatusByLocation[pLocation.Key])
			.map((pLocation) => ({ Value: pLocation.Key, Label: pLocation.Name, Sub: [ pLocation.Type, pLocation.QRCode ].filter(Boolean).join(' · ') }));
		return libCustomerConfigScreen.filterChoices(tmpChoices, pText);
	}

	/**
	 * Give the picked location the status chosen for new rows.
	 *
	 * @param {string} pPicker
	 * @param {string} pValue - a location key
	 */
	pickerChose(pPicker, pValue)
	{
		if (this.state.NewStatus && !this.state.StatusByLocation[pValue])
		{
			this.state.StatusByLocation[pValue] = this.state.NewStatus;
			this._receiveEdited();
		}
	}

	// --- Rendering ------------------------------------------------------------------------------------------------

	/**
	 * @param {string} pHash
	 * @return {string}
	 */
	_statusName(pHash)
	{
		if (pHash === NO_STATUS)
		{
			return 'No status yet';
		}
		const tmpStatus = this.state.Statuses.find((pStatus) => pStatus.Hash === pHash);
		return tmpStatus ? tmpStatus.Name : pHash;
	}

	/**
	 * @param {string} pSelected
	 * @param {string} pOnChange - the inline handler, taking this.value
	 * @return {string}
	 */
	_statusSelectHTML(pSelected, pOnChange)
	{
		const tmpKnown = this.state.Statuses.some((pStatus) => pStatus.Hash === pSelected);
		const tmpOptions = this.state.Statuses.map((pStatus) => `<option value="${this._esc(pStatus.Hash)}" ${pStatus.Hash === pSelected ? 'selected' : ''}>${this._esc(pStatus.Name)}</option>`).join('');
		const tmpUnknown = (pSelected && !tmpKnown) ? `<option value="${this._esc(pSelected)}" selected>${this._esc(pSelected)} (not a status this customer has)</option>` : '';
		return `<select class="ccs-select" onchange="${pOnChange}">${tmpUnknown}${tmpOptions}</select>`;
	}

	/** @return {string} */
	_scannerHTML()
	{
		const tmpState = this.state;
		const tmpUnset = (tmpState.Scanner === null) ?
			'<div class="ccs-note">Not set for this customer yet. iPads keep whatever they had before, and a new install doesn’t show the button.</div>' :
			'';
		return `<div class="ccs-section">
			<h2 class="ccs-section-title">Scanning in Fieldbook on iPad</h2>
			<p class="ccs-help">The Scan QR Code button opens a sample by scanning its label. iPads pick up a change the next time they sync.</p>
			${tmpUnset}
			<label class="ccs-check"><input type="checkbox" ${tmpState.Scanner === true ? 'checked' : ''} onchange="_Pict.providers.${this.providerHash}.setScanner(this.checked)"> Show the Scan QR Code button</label>
		</div>`;
	}

	/** @return {string} */
	_receiveHTML()
	{
		const tmpState = this.state;
		const tmpByKey = new Map(tmpState.Locations.map((pLocation) => [ pLocation.Key, pLocation ]));
		const tmpRows = Object.keys(tmpState.StatusByLocation)
			.map((pKey) => ({ Key: pKey, Location: tmpByKey.get(pKey) || null }))
			.sort((pA, pB) => (pA.Location ? pA.Location.Name : pA.Key).localeCompare(pB.Location ? pB.Location.Name : pB.Key))
			.map((pRow) =>
			{
				const tmpName = pRow.Location ?
					`${this._esc(pRow.Location.Name)}<span class="ccs-sub">${this._esc([ pRow.Location.Type, pRow.Location.QRCode ].filter(Boolean).join(' · '))}</span>` :
					`${this._esc(pRow.Key)}<span class="ccs-sub ccs-warn">No location has this code, so this rule does nothing.</span>`;
				return `<tr>
					<td>${tmpName}</td>
					<td>${this._statusSelectHTML(tmpState.StatusByLocation[pRow.Key], `_Pict.providers.${this.providerHash}.setLocationStatus(${this._arg(pRow.Key)}, this.value)`)}</td>
					<td><button class="ccs-btn is-danger" onclick="_Pict.providers.${this.providerHash}.removeLocation(${this._arg(pRow.Key)})">Remove</button></td>
				</tr>`;
			}).join('');
		const tmpTable = tmpRows ?
			`<table class="ccs-table"><thead><tr><th>Location</th><th>Sets the sample’s status to</th><th></th></tr></thead><tbody>${tmpRows}</tbody></table>` :
			'<div class="ccs-note">No locations set a status, so scanning a sample into a location only records where it is.</div>';
		const tmpTopLeft = tmpState.Locations.filter((pLocation) => pLocation.IsTop && !tmpState.StatusByLocation[pLocation.Key]).length;
		const tmpAllowed = Array.isArray(tmpState.OnlySetFrom) ? tmpState.OnlySetFrom : libScanStatus.DEFAULT_ONLY_SET_FROM;
		const tmpFrom = [ NO_STATUS ].concat(tmpState.Statuses.map((pStatus) => pStatus.Hash)).map((pHash) =>
			`<label class="ccs-check"><input type="checkbox" ${tmpAllowed.includes(pHash) ? 'checked' : ''} onchange="_Pict.providers.${this.providerHash}.toggleOnlySetFrom(${this._arg(pHash)}, this.checked)"> ${this._esc(this._statusName(pHash))}</label>`).join('');
		return `<div class="ccs-section">
			<h2 class="ccs-section-title">Receiving samples by scanning them into a location</h2>
			<p class="ccs-help">When a sample is scanned into one of these locations, it takes the status shown, for example Received at a lab’s reception. Locations not listed don’t change a sample’s status.</p>
			${tmpTable}
			<div class="ccs-row">
				<span>Add a location that sets</span>
				${this._statusSelectHTML(tmpState.NewStatus, `_Pict.providers.${this.providerHash}.setNewStatus(this.value)`)}
				${this.pickerHTML('Location', 'Search locations by name or code')}
			</div>
			<div class="ccs-row">
				<button class="ccs-btn" ${tmpTopLeft ? '' : 'disabled'} onclick="_Pict.providers.${this.providerHash}.addTopLevelLocations()">Add every top-level location (${tmpTopLeft})</button>
				<span class="ccs-sub">Top-level locations are the ones not inside another, such as each lab.</span>
			</div>
			<label class="ccs-label">Only change samples whose status is</label>
			<p class="ccs-help">So that scanning a sample that is already being tested can’t send it back to an earlier status.${Array.isArray(tmpState.OnlySetFrom) ? '' : ' Not set yet, so only samples with no status are changed.'}</p>
			<div class="ccs-checks">${tmpFrom}</div>
		</div>`;
	}

	/** @return {string} */
	bodyHTML()
	{
		return `${this._scannerHTML()}${this._receiveHTML()}`;
	}
}

SampleScanningConfigProvider.SCREEN =
{
	Hash: 'SampleScanningConfig',
	StateKey: 'SampleScanningConfig',
	Mount: '#SSC-Detail',
	Entitlement: { Entity: 'SampleQRCode', Action: 'Configure', Label: 'Configure sample QR codes, labels and scanning' },
};

module.exports = SampleScanningConfigProvider;
module.exports.default_configuration = { ProviderIdentifier: 'SampleScanningConfig', AutoInitialize: true, AutoInitializeOrdinal: 0 };
module.exports.buildReceiveConfig = buildReceiveConfig;
module.exports.buildFeaturesConfig = buildFeaturesConfig;
module.exports.locationKey = locationKey;
